<template>
  <div class="mb-16" v-if="eventID && checkoutStore.event">
    <CheckoutHeader :current-step="1" />
    <!-- <CheckoutLayout
      next-link="/checkout/addons"
      :title="$t('checkout.choose_tickets')"
      :has-discount-code="true"
      @on-code-change="handleCodeChange"
    > -->

    <CheckoutLayout
      next-link="/checkout/information"
      :title="$t('checkout.choose_tickets')"
      :has-discount-code="true"
      @on-code-change="handleCodeChange"
    >
      <template #header v-if="showHeader">
        <CheckoutTicketHeader :eventItem="eventItem" />
      </template>

      <div
        v-if="!cart.items.length"
        class="bg-surface-grey-light p-4 rounded-sm flex gap-4 font-semibold"
      >
        <CartIcon class="h-6 w-6 fill-primary" />
        {{ $t('globals.cart_empty') }}
      </div>
      <div class="space-y-4">
        <BaseSlideTransition>
          <CheckoutTicketsNotice v-if="checkoutStore.noticeTickets.length > 0 && checkoutStore.vouchercode && checkoutStore.vouchercode.code && checkoutStore.vouchercode.code.length > 0">
            <span v-if="checkoutStore.noticeTickets.length > 1">
              {{
                $t('checkout.extra_tickets_shown', {
                  count: checkoutStore.noticeTickets.length,
                })
              }}
            </span>
            <span v-else>
              {{
                $t('checkout.extra_ticket_shown')
              }}
            </span>
            <span class="ml-1" v-if="checkoutStore.vouchercode && checkoutStore.vouchercode.code && checkoutStore.vouchercode.code.length > 0">
              {{ checkoutStore.vouchercode.code }}
            </span>
          </CheckoutTicketsNotice>
        </BaseSlideTransition>

        <CheckoutTicket
          v-for="ticket in checkoutStore.tickets"
          :key="ticket.id"
          :ticket="ticket"
          :event="checkoutStore.event"
          :eventID="eventID"
        />
      </div>
    </CheckoutLayout>
  </div>
</template>

<script setup lang="ts">
  import { useHead } from '@unhead/vue';
  import { computed, onMounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRoute } from 'vue-router';

  //import { useGeneralStore } from '@/stores/general';
  import { fetchEvent } from '@/api/event';
  import CheckoutHeader from '@/components/checkout/CheckoutHeader.vue';
  import CheckoutLayout from '@/components/checkout/CheckoutLayout.vue';
  import CheckoutTicket from '@/components/checkout/CheckoutTicket.vue';
  import CheckoutTicketHeader from '@/components/checkout/CheckoutTicketHeader.vue';
  import CheckoutTicketsNotice from '@/components/checkout/CheckoutTicketsNotice.vue';
  import CartIcon from '@/components/icons/CartIcon.vue';
  import BaseSlideTransition from '@/components/ui/BaseSlideTransition.vue';
  import { ticketItems } from '@/data';
  import { useCartStore } from '@/stores/cart';
  import { useCheckoutStore } from '@/stores/checkoutStore';
  import { useToastStore } from '@/stores/toastStore';
  import type { CodeItem, EventDetail } from '@/types';
  import {
    DISCOUNT_STORAGE_KEY,
    loadCodesFromStorage,
  } from '@/utils/loadCodesFromLocalStorage';
  import { useWebsocketStore } from '@/websocket/websocket';

  const { t } = useI18n();
  const websocketStore = useWebsocketStore();
  const route = useRoute();
  const checkoutStore = useCheckoutStore();
  const cart = useCartStore();
  const subscribeEventSent = ref(false);
  //const generalStore = useGeneralStore();
  const toastStore = useToastStore();

  // TODO dont get events from general. use data from /of2/be/event endpoint (new store?)
  // Get event from general store
  const eventID = computed(() => checkoutStore.getCheckoutID());
  const eventItem = ref<EventDetail>({} as EventDetail);

  function initEvent(id: number) {
    fetchEvent(id).then(data => {
      eventItem.value = data || ({} as EventDetail);
    });
    cart.addToCart(id);
    if (!subscribeEventSent.value) {
      websocketStore.send('subscribeevent', { id }, undefined);
      subscribeEventSent.value = true;
    }
  }

  if (eventID.value) {
    initEvent(eventID.value);
  } else {
    // in case of no eventID, route to home
    window.location.href = '/';
  }

  watch(() => checkoutStore.checkoutID, (newID) => {
    if (newID) {
      subscribeEventSent.value = false;
      initEvent(newID);
    }
  });
  //const eventItem = generalStore.events().find(e => e.id === eventID) || emptyEventItem;

  //Computed value to show header depending on if eventItem is loaded or not, to avoid errors in child components
  const showHeader = computed(() => {
    return !!eventItem.value;
  });

  

  useHead({
    title: t('checkout.choose_tickets'),
    meta: [
      {
        property: 'og:title',
        content: t('checkout.choose_tickets'),
      },
    ],
  });

  const activeCodes = ref<CodeItem[]>([]);
  //const noticeTickets = ref<Ticket[]>([]);

  onMounted(() => {
    if (route.query.ticketId) {
      const ticketId = parseInt(route.query.ticketId as string);
      const ticket = ticketItems.find(t => t.id === ticketId);
      if (ticket) {
        cart.addToCart(ticket.id);
      }
    }

    websocketStore.open();
    if( eventID.value && !subscribeEventSent.value ){
      websocketStore.send('subscribeevent', { id: eventID.value }, undefined);
      subscribeEventSent.value = true;
    }
  });

  function loadTickets() {
    activeCodes.value = loadCodesFromStorage(DISCOUNT_STORAGE_KEY);
    /*
    const newTickets = ticketItems.filter(ticket => {
      if (!ticket.activationCode) return true;

      return activeCodes.value.some(
        code =>
          code.code.toLowerCase() === ticket.activationCode?.toLocaleLowerCase()
      );
    });

    noticeTickets.value = newTickets.filter(
      ticket =>
        ticket.activationCode &&
        !initialTickets.value.some(x => x.id === ticket.id)
    );*/ // Disabled for now - always show all tickets, vouchercode handling here

    //tickets.value = newTickets;
  }

  function handleCodeChange() {
    loadTickets();
  }

  //Watch cart.differentCount and if it changes to true, show a toast informing the user they could not reserve the specified amount
  watch(() => cart.differentCount, newValue => {
    if (newValue) {
      const message = cart.differentCountMessage.message;
      const count = cart.differentCountMessage.count;
      const ticketsText = (count == 1 ? t('common.ticket') : t('common.ticket_plural')).toLocaleLowerCase();
      console.log('Cart differentCount is true, showing toast with message:', message, 'and count:', count);
      if(message && message == 'initial'){
        toastStore.showToast(t('checkout.reservation_mismatch', {tickets: ticketsText}), 15000);
      }
      else if(message && count){
        toastStore.showToast(t('checkout.reservation_mismatch_' + message, { count, tickets: ticketsText }), 15000);
      }
      cart.differentCount = false; // reset the flag
    }
  });

  onMounted(() => {
    //tickets.value = ticketItems.filter(ticket => !ticket.activationCode); // This is for vouchercodes

    setTimeout(() => {
      loadTickets();
    }, 0);
    loadTickets();
  });
</script>
